<template>
  <component
    :is="rootTag"
    v-if="visible"
    v-tooltip="crownTooltipDirective"
    class="lead-quality-crown"
    :class="{
      'lead-quality-crown--muted': showAsMuted,
      'lead-quality-crown--interactive': opensResultModal,
      'lead-quality-crown--cta-context': tooltipCtaOnly,
    }"
    :type="opensResultModal ? 'button' : undefined"
    :title="nativeHoverTitle"
    :role="opensResultModal ? 'button' : 'img'"
    :aria-label="ariaLabelText"
    @click="onRootClick"
  >
    <svg
      class="lead-quality-crown-svg"
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        d="M5 16L3 7l5.5 3L12 4l3.5 6L21 7l-2 9H5zm2.35-2h9.3l.75-3.3L14 12l-2-3.5L10 12l-3.1-1.3L7.35 14z"
      />
    </svg>
  </component>
</template>

<script>
import { mapGetters } from 'vuex';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  shouldShowLeadQualityCrown,
  leadQualityCrownThreshold,
  leadQualityScoreFromMetadata,
  leadQualityReasonsFromMetadata,
} from 'dashboard/helpers/leadQuality';

export default {
  name: 'LeadQualityCrown',
  props: {
    accountId: { type: [Number, String], required: true },
    /** Stored lead_quality_metadata from contact API */
    metadata: { type: Object, default: null },
    /**
     * When true (all ContactInfo views: modals, full profile, conversation panel), any numeric score
     * shows a crown; scores strictly below the crown threshold use gray, at or above use gold.
     */
    allowMutedCrownBelowThreshold: {
      type: Boolean,
      default: false,
    },
    /**
     * When true, clicking the crown emits `open-lead-quality-result` (parent opens result modal).
     */
    opensResultModal: {
      type: Boolean,
      default: false,
    },
    /**
     * When true (ContactInfo / contact “more details” views only), tooltip shows a short CTA
     * instead of score, reasons, and threshold note.
     */
    tooltipCtaOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * v-tooltip placement when set (e.g. `right` in tight / overflow-prone layouts).
     * Empty keeps the default simple string tooltip behavior for existing callers.
     */
    tooltipPlacement: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapGetters({
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    rootTag() {
      return this.opensResultModal ? 'button' : 'span';
    },
    visible() {
      if (
        !this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.LEAD_QUALITY
        )
      ) {
        return false;
      }
      if (this.allowMutedCrownBelowThreshold) {
        return leadQualityScoreFromMetadata(this.metadata) != null;
      }
      return shouldShowLeadQualityCrown(this.metadata);
    },
    showAsMuted() {
      if (!this.allowMutedCrownBelowThreshold) return false;
      const score = leadQualityScoreFromMetadata(this.metadata);
      if (score == null) return false;
      return score < leadQualityCrownThreshold();
    },
    crownTooltipDirective() {
      if (!this.tooltipPlacement) {
        return this.tooltipText;
      }
      return {
        content: this.tooltipText,
        placement: this.tooltipPlacement,
        container: 'body',
        classes: ['vue-tooltip-theme', 'tooltip-preline'],
        delay: { show: 150, hide: 0 },
        popperOptions: {
          modifiers: {
            preventOverflow: {
              boundariesElement: 'viewport',
            },
            flip: {
              enabled: true,
            },
          },
        },
      };
    },
    /** Avoid browser duplicate tooltip when using advanced v-tooltip + placement */
    nativeHoverTitle() {
      return this.tooltipPlacement ? undefined : this.tooltipText;
    },
    tooltipText() {
      if (this.tooltipCtaOnly) {
        return (
          this.$t('LEAD_QUALITY.CROWN_TOOLTIP_MODAL_ONLY') ||
          'Click the crown to view reasons and more details.'
        );
      }
      const score = leadQualityScoreFromMetadata(this.metadata);
      const reasons = leadQualityReasonsFromMetadata(this.metadata);
      const threshold = leadQualityCrownThreshold();
      const scorePart =
        score != null ? `${this.$t('LEAD_QUALITY.SCORE_LABEL')}: ${score}` : '';
      const note = this.$t('LEAD_QUALITY.CROWN_TOOLTIP_NOTE', { threshold });
      const main = reasons
        ? [scorePart, reasons].filter(Boolean).join(' — ')
        : scorePart ||
          this.$t('LEAD_QUALITY.GENUINE_LEAD') ||
          'High-quality lead';
      return [main, note].filter(Boolean).join('\n\n');
    },
    ariaLabelText() {
      if (this.tooltipCtaOnly) return this.tooltipText;
      if (this.opensResultModal) {
        return (
          this.$t('LEAD_QUALITY.CROWN_OPEN_RESULT') ||
          this.tooltipText ||
          this.$t('LEAD_QUALITY.SCORE_LABEL')
        );
      }
      return this.tooltipText;
    },
  },
  methods: {
    onRootClick(e) {
      if (!this.opensResultModal) return;
      e.stopPropagation();
      this.$emit('open-lead-quality-result');
    },
  },
};
</script>

<style scoped>
.lead-quality-crown {
  display: inline-flex;
  align-items: center;
  color: #ca8a04;
  flex-shrink: 0;
  vertical-align: middle;
}
.lead-quality-crown-svg {
  display: block;
}

/* Slightly larger crown in ContactInfo / “more details” (matches tooltipCtaOnly). */
.lead-quality-crown--cta-context .lead-quality-crown-svg {
  width: 20px;
  height: 20px;
}

.lead-quality-crown--muted {
  color: #94a3b8;
}

body.dark .lead-quality-crown--muted {
  color: #64748b;
}

button.lead-quality-crown.lead-quality-crown--interactive {
  border: none;
  padding: 0;
  margin: 0;
  background: transparent;
  cursor: pointer;
  font: inherit;
  line-height: 0;
  border-radius: 0.25rem;
}

button.lead-quality-crown.lead-quality-crown--interactive:focus-visible {
  outline: 2px solid rgb(59 130 246);
  outline-offset: 2px;
}
</style>
